import React from 'react';
import { Percent } from 'lucide-react';

interface InterestRatesEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const InterestRatesEditor: React.FC<InterestRatesEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Bankaların Faiz Oranları'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Alt Başlık
            </label>
            <input
              type="text"
              name="subtitle"
              value={settings?.subtitle || 'Güncel kredi faiz oranlarını karşılaştırın'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Ürün Türü
          </label>
          <select
            name="productType"
            value={settings?.productType || 'loan'}
            onChange={handleSettingChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          >
            <option value="loan">Kredi</option>
            <option value="deposit">Mevduat</option>
          </select>
        </div>
        
        {settings?.productType === 'loan' && (
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Kredi Türü
            </label>
            <select
              name="loanType"
              value={settings?.loanType || 'ihtiyac'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="ihtiyac">İhtiyaç Kredisi</option>
              <option value="konut">Konut Kredisi</option>
              <option value="tasit">Taşıt Kredisi</option>
              <option value="kobi">Kobi Kredisi</option>
            </select>
          </div>
        )}
        
        {settings?.productType === 'deposit' && (
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Mevduat Türü
            </label>
            <select
              name="depositType"
              value={settings?.depositType || 'tl'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="tl">TL Mevduat</option>
              <option value="usd">USD Mevduat</option>
              <option value="eur">EUR Mevduat</option>
            </select>
          </div>
        )}
      </div>
      
      {/* Display Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Görünüm Ayarları</h3>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showTabs"
              checked={settings?.showTabs !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Sekmeleri Göster</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showDisclaimer"
              checked={settings?.showDisclaimer !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Yasal Uyarıyı Göster</span>
          </label>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Yasal Uyarı Metni
          </label>
          <textarea
            name="disclaimerText"
            value={settings?.disclaimerText || '* Faiz oranları bankaların resmi web sitelerinden alınmıştır ve bilgilendirme amaçlıdır. Kredinin toplam maliyeti; kredi tutarı, vade ve bankanın sunduğu özel faiz oranına göre değişiklik gösterebilir.'}
            onChange={handleSettingChange}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>
      
      {/* Preview */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Percent size={16} className="text-[#F7BA45]" />
          <h4 className="text-sm font-medium text-slate-700">Önizleme</h4>
        </div>
        <p className="text-sm text-slate-600">
          Faiz oranları tablosu, bankaların 
          {settings?.productType === 'loan' 
            ? ` ${settings?.loanType === 'ihtiyac' ? 'ihtiyaç kredisi' : 
                settings?.loanType === 'konut' ? 'konut kredisi' : 
                settings?.loanType === 'tasit' ? 'taşıt kredisi' : 'kobi kredisi'} ` 
            : ` ${settings?.depositType === 'tl' ? 'TL mevduat' : 
                settings?.depositType === 'usd' ? 'USD mevduat' : 'EUR mevduat'} `}
          için güncel faiz oranlarını gösterir.
        </p>
      </div>
    </div>
  );
};

export default InterestRatesEditor;